import React, { useState } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { WizardPanel } from '../../wizard/components/WizardFrame';
import ReviewTable, { IReviewRow } from '../../wizard/kit/ReviewTable';
import { TextField } from '../../wizard/kit/fields';
import { ErrorList, Words } from '../../wizard/kit/layout';
import { PrimaryButton, SecondaryButton } from '../../wizard/wizardStyles';
import { ConfigIntent } from '../configSubmission';

export const TC = `${TOrbit}.configStudio`;

interface IConfigStepPanelProps {
  title: string;
  step: number;
  total: number;
  question: string;
  /** What still blocks Next; listed under the form once Next has been pressed. */
  errors?: string[];
  /** `null` on a tile pick, which advances by itself and draws no Next (46, 45, 56). */
  onNext: (() => void) | null;
  onBack: () => void;
  /** The last input step says "Finalise" (54, 64). */
  last?: boolean;
  testId: string;
  children: React.ReactNode;
}

/** A numbered step in the 950 panel of frames 45-70, "Step n/total" over a `total`-segment bar. */
export const ConfigStepPanel: React.FC<IConfigStepPanelProps> = ({
  title,
  step,
  total,
  question,
  errors = [],
  onNext,
  onBack,
  last = false,
  testId,
  children
}) => {
  const { t } = useMultiLanguageTranslation();
  const [attempted, setAttempted] = useState(false);
  const shown = attempted ? errors : [];

  return (
    <WizardPanel
      title={title}
      step={step}
      total={total}
      narrow
      onBack={onBack}
      question={question}
      testId={testId}
      footer={
        onNext === null ? null : (
          <PrimaryButton
            type="button"
            onClick={() => {
              setAttempted(true);
              if (errors.length === 0) {
                onNext();
              }
            }}
          >
            {t(`${TC}.${last ? 'finalise' : 'next'}`)}
          </PrimaryButton>
        )
      }
    >
      {children}
      {shown.length > 0 ? (
        <ErrorList role="alert">
          {shown.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ErrorList>
      ) : null}
    </WizardPanel>
  );
};

// The campaign review's name row (6778:21045): a 198 label column, then the 460 input.
const NameRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  > :first-child {
    flex: 0 0 198px;
  }
`;

interface IConfigReviewPanelProps<E> {
  title: string;
  question: string;
  onBack: () => void;
  nameLabel: string;
  namePlaceholder: string;
  nameRequired: string;
  name: string;
  onName: (name: string) => void;
  rows: IReviewRow<E>[];
  onEdit: (target: E) => void;
  /** "Finalise" on the rule review (55), "Confirm" on the reward review (66). */
  confirmLabel: string;
  onSubmit: (intent: ConfigIntent) => Promise<void> | void;
  testId: string;
}

/** A review (55, 66): name the record, check the answers, then save it as a draft or confirm. */
export const ConfigReviewPanel = <E,>({
  title,
  question,
  onBack,
  nameLabel,
  namePlaceholder,
  nameRequired,
  name,
  onName,
  rows,
  onEdit,
  confirmLabel,
  onSubmit,
  testId
}: IConfigReviewPanelProps<E>) => {
  const { t } = useMultiLanguageTranslation();
  const [nameMissing, setNameMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Both buttons lock while a save is in flight, so one click sends one save. A failed save keeps
  // every answer and says why, as the campaign review does.
  const submit = async (intent: ConfigIntent) => {
    if (name.trim() === '') {
      setNameMissing(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSubmit(intent);
    } catch (error) {
      const reason = error instanceof Error ? error.message : '';
      setSaveError(reason !== '' ? reason : t(`${TC}.saveFailed`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardPanel
      title={title}
      step={null}
      narrow
      onBack={onBack}
      question={question}
      testId={testId}
      spreadFooter
      footer={
        <>
          <SecondaryButton type="button" disabled={saving} onClick={() => submit('draft')}>
            {t(`${TC}.saveDraft`)}
          </SecondaryButton>
          <PrimaryButton type="button" disabled={saving} onClick={() => submit('confirm')}>
            {confirmLabel}
          </PrimaryButton>
        </>
      }
    >
      <NameRow>
        <Words $muted>{nameLabel}</Words>
        <TextField
          label={nameLabel}
          placeholder={namePlaceholder}
          width="xl"
          value={name}
          onChange={value => {
            setNameMissing(false);
            onName(value);
          }}
        />
      </NameRow>
      <ReviewTable<E> rows={rows} onEdit={onEdit} />
      {nameMissing || saveError !== null ? (
        <ErrorList role="alert">
          {nameMissing ? <li>{nameRequired}</li> : null}
          {saveError !== null ? <li>{saveError}</li> : null}
        </ErrorList>
      ) : null}
    </WizardPanel>
  );
};
